import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { opportunityService } from '../services/opportunityService';

const AssociatedOpportunities = ({ companyId, contactId, title = "Associated Opportunities" }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Sorting state - default sort by Created Date descending
  const [orderBy, setOrderBy] = useState('createdDate');
  const [order, setOrder] = useState('desc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  const loadOpportunities = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      let response;
      
      if (companyId) {
        response = await opportunityService.getOpportunitiesByCompany(
          companyId, 
          page + 1, 
          rowsPerPage, 
          orderBy, 
          order
        );
      } else if (contactId) {
        response = await opportunityService.getOpportunitiesByContact(
          contactId, 
          page + 1, 
          rowsPerPage, 
          orderBy, 
          order
        );
      }

      if (response && response.data) {
        setOpportunities(response.data);
        setTotalCount(response.totalCount || 0);
      }
    } catch (err) {
      console.error('Error loading opportunities:', err);
      setError(`Failed to load opportunities: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [companyId, contactId, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    if (companyId || contactId) {
      loadOpportunities();
    } else {
      setLoading(false);
    }
  }, [companyId, contactId, loadOpportunities]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatProbability = (value) => {
    if (value === null || value === undefined) return '';
    return `${value}%`;
  };

  const handleOpportunityClick = (opportunityId) => {
    window.open(`/opportunities/${opportunityId}`, '_blank', 'noopener,noreferrer');
  };

  // Define columns based on screen size
  const columns = isMobile ? [
    { id: 'opportunityName', label: 'Name', sortable: true },
    { id: 'dealStage', label: 'Stage', sortable: true }
  ] : [
    { id: 'opportunityName', label: 'Name', sortable: true },
    { id: 'dealStage', label: 'Stage', sortable: true },
    { id: 'createdDate', label: 'Created Date', sortable: true }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title} ({totalCount})
      </Typography>
      
      {opportunities.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            No opportunities found.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {opportunities.map((opportunity) => (
                  <TableRow 
                    key={opportunity.id || opportunity.opportunityId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpportunityClick(opportunity.id || opportunity.opportunityId)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {opportunity.opportunityName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {opportunity.dealStage}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(opportunity.createdDate)}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AssociatedOpportunities; 